import {DropdownOptionProps} from '@wix/wix-ui-tpa';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import React from 'react';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {RenderContext, withRenderContext, type WithRenderContextProps} from '../../../providers/renderContext';
import {SPECS} from '../../../specs';
import {LazyDropdown} from '../../lazyComponents/lazyWUTComponents';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import s from './OptionsDropdown.scss';
import {st, classes as stylableClasses} from './OptionsDropdown.st.css';

export interface OptionsDropdownProps extends ProvidedGlobalProps, IProvidedTranslationProps, WithRenderContextProps {
  optionIndex: number;
  showErrors?: boolean;
}

export enum OptionsDropdownDataHook {
  OptionsDropdownContainer = 'options-dropdown-container',
  OptionsDropdown = 'options-dropdown',
  OptionsDropdownTitle = 'options-dropdown-title',
}

class OptionsDropdownComponent extends React.Component<OptionsDropdownProps> {
  private readonly dropdownContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: OptionsDropdownProps) {
    const {
      optionIndex,
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget =
        UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
        optionIndex === scrollAndFocusInputTarget?.index;

      if (isScrollAndFocusInputTarget && this.dropdownContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.Selection, this.dropdownContainerRef.current, isQuickView);
      }
    }
  }

  private readonly handleChange = ({id: selectionId}: DropdownOptionProps) => {
    const {
      optionIndex,
      renderContext,
      globals: {handleUserInput, validateUserInputsChanges},
    } = this.props;

    const nextSelection = this.productOption.selections.find(({id}) => id.toString() === selectionId);

    handleUserInput(UserInputType.Selection, nextSelection, optionIndex, renderContext === RenderContext.StickyDrawer);

    validateUserInputsChanges();
  };

  private readonly isSelectionAvailable = (isSelectable: boolean): boolean => {
    const {
      globals: {isBackInStockEnabled},
    } = this.props;

    return isBackInStockEnabled ? !isSelectable : false;
  };

  private get activeSelection() {
    const {
      optionIndex,
      globals: {userInputs},
    } = this.props;
    return userInputs[UserInputType.Selection][optionIndex];
  }

  private get productOption() {
    const {
      optionIndex,
      globals: {product},
    } = this.props;

    return product.options[optionIndex];
  }

  private get isGroupedByOption(): boolean {
    const {productGroup, isCombinedListing} = this.props.globals;
    return !!isCombinedListing && this.productOption.id === productGroup?.groupingCustomizationId;
  }

  private get dropdownOptions(): DropdownOptionProps[] {
    const {
      globals: {variantInfo, isBackInStockEnabled},
    } = this.props;

    const getSelectionAvailabilityInfo = (selectionId: number) =>
      variantInfo.selectionsAvailability[this.productOption.id][selectionId];

    return this.productOption.selections
      .filter((selection) => this.isGroupedByOption || getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => ({
        id: selection.id.toString(),
        isSelectable:
          this.isGroupedByOption ||
          (isBackInStockEnabled ? true : getSelectionAvailabilityInfo(selection.id).isSelectable),
        value: selection.description,
        unavailable: this.isGroupedByOption
          ? false
          : this.isSelectionAvailable(getSelectionAvailabilityInfo(selection.id).isSelectable),
      }));
  }

  public state: Readonly<{labelForId: string}> = {
    labelForId: undefined,
  };

  public render(): JSX.Element {
    const {
      t,
      optionIndex,
      showErrors = true,
      globals: {isProductSubmitted, userInputErrors, isQuickView, useExperiments, isSSR},
      renderContext,
    } = this.props;

    const {title, id: dropdownId} = this.productOption;
    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    const isFixDropdownMobileSsr = useExperiments.enabled(SPECS.FixProductPageDropdownMobileSsr);
    const selectedId = this.activeSelection?.id?.toString();
    const mobileNativeSelect = isFixDropdownMobileSsr ? !isSSR : true;

    const errorProps =
      showErrors || !isStickyAddToCartEnabled
        ? {
            newErrorMessage: true,
            error: isProductSubmitted && userInputErrors[UserInputType.Selection][optionIndex],
            errorMessage: t('SELECT_OPTION_WARNING', {optionTitle: title}),
          }
        : {};

    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === RenderContext.StickyDrawer;

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const dropdownRender = () => {
      const selectionProps = isStickyAddToCartEnabled
        ? {selectedId}
        : {initialSelectedId: (this.activeSelection?.id || '').toString()};

      return (
        <LazyDropdown
          className={classNames(
            st(stylableClasses.stylableRoot, {isQuickView, stickyDrawer: isStickyDrawer}),
            s.dropdownListNewStyling
          )}
          dropdownId={dropdownId}
          label={title}
          aria-label={title}
          options={this.dropdownOptions}
          onChange={this.handleChange}
          placeholder={t('SELECT_PLACE_HOLDER')}
          {...selectionProps}
          data-hook={OptionsDropdownDataHook.OptionsDropdown}
          mobileNativeSelect={mobileNativeSelect}
          {...errorProps}
          aria-required
          showAsterisk
        />
      );
    };

    return (
      <div data-hook={OptionsDropdownDataHook.OptionsDropdownContainer} ref={this.dropdownContainerRef}>
        {dropdownRender()}
      </div>
    );
  }
}

export const OptionsDropdown = withGlobalProps(
  withTranslations('globals.texts')(withRenderContext(OptionsDropdownComponent))
);
